import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMountEffect } from '../../hooks/useMountEffect'

import type { AxiosError } from 'axios'
import { Building2, Check, Loader2 } from 'lucide-react'
import { RightPanel, type RightPanelState } from './RightPanel'
import { applyRecommendation } from '../../services/recommendations_api'
import { notifySuccess } from '../../services/toast'
import { cn } from '../UI/cn'
import {
  getLatestActionSummary,
  getRecommendationRefreshStatus,
  ActionSummaryData,
} from '../../services/recommendations_api'
import type { EnrichedAction } from '../../services/recommendations_contracts'
import {
  ApplyRecommendationPanel,
  EnrichedActionCard,
  EnrichedActionCardSkeleton,
} from './EnrichedActionCard'
import { SummaryStatsHeader } from './SummaryStatsHeader'
import { URGENCY_ICONS, URGENCY_COLORS } from './constants'
import { resolveAgentIcon } from './agentIcons'
import useApiService from '../../services/api'
import { useRecommendationsPolling } from './useRecommendationsPolling'
import { useActionClickManager } from './useActionClickManager'
import { useAggregationRefresh } from '../DataTable/hooks/useAggregationRefresh'
import {
  groupActionsByAgent,
  groupActionsByCompany,
  partitionActionsByStatus,
  sortByUrgencyScore,
} from './recommendationVisibility'

type ViewMode = 'priority' | 'signal' | 'company'

const URGENCY_BANDS: Array<{
  key: EnrichedAction['urgency']
  label: string
}> = [
  { key: 'do_now', label: 'Hot' },
  { key: 'today', label: 'Today' },
  { key: 'this_week', label: 'This Week' },
]

interface RecommendationsCardProps {
  className?: string
  /** Optional user ID to view another user's summary (superusers only) */
  userId?: number
  /** Callback fired whenever the summary changes */
  onSummaryLoaded?: (summary: ActionSummaryData | null) => void
  /** Content rendered above the stats header in the fixed zone (e.g. sidebar title) */
  headerSlot?: React.ReactNode
  /** Controlled right-panel state. Tagged union so the card can ask
   *  the parent to open different kinds of panels (recently-completed,
   *  apply-rec, etc.) without lifting per-kind state. Falls back to
   *  internal state when omitted so the card still works standalone. */
  activePanel?: RightPanelState
  onActivePanelChange?: (next: RightPanelState) => void
  /** When the parent provides this ref, the right panel content is
   *  portaled into ``ref.current`` so it lives as a flex sibling of
   *  the parent's main content (no fixed-position clipping artifacts
   *  against the App Shell's rounded corners). */
  resolvedSiderHostRef?: React.RefObject<HTMLDivElement | null>
}

/**
 * Displays AI-generated daily recommendations for the current user.
 *
 * Shows prioritized action items grouped by urgency (default), signal type, or company.
 * All three views share the same flat design language: inline group headers + card lists.
 *
 * Superusers can pass a userId prop to view another user's summary.
 */
export const RecommendationsCard: React.FC<RecommendationsCardProps> = ({
  className,
  userId,
  onSummaryLoaded,
  headerSlot,
  activePanel: activePanelProp,
  onActivePanelChange,
  resolvedSiderHostRef,
}) => {
  const queryClient = useQueryClient()
  const [viewMode, setViewMode] = useState<ViewMode>('priority')
  // Controlled when the parent supplies ``activePanel`` + setter;
  // otherwise falls back to internal state so the component still
  // works standalone (e.g. component-catalog previews, tests).
  const [internalActivePanel, setInternalActivePanel] =
    useState<RightPanelState>({ kind: 'closed' })
  const isControlled =
    activePanelProp !== undefined && onActivePanelChange !== undefined
  const activePanel = isControlled ? activePanelProp : internalActivePanel
  const setActivePanel = useCallback(
    (next: RightPanelState) => {
      if (isControlled && onActivePanelChange) {
        onActivePanelChange(next)
      } else {
        setInternalActivePanel(next)
      }
    },
    [isControlled, onActivePanelChange]
  )
  // Convenience accessor — the recently-completed button toggles
  // between this kind and ``closed``.
  const showResolved = activePanel.kind === 'recently-completed'
  const setShowResolved = useCallback(
    (open: boolean) => {
      setActivePanel(open ? { kind: 'recently-completed' } : { kind: 'closed' })
    },
    [setActivePanel]
  )

  // Capture the parent-provided sider host element after mount so the
  // portal has a target to render into. The ref is null during the
  // initial render (refs populate during commit); useMountEffect runs
  // post-commit, sets state, triggers a re-render with the target.
  //
  // Reviewer note (do not "fix"): past reviews have suggested swapping
  // useMountEffect for ``useEffect(..., [resolvedSiderHostRef])`` to
  // handle remount. Two reasons not to: (1) client/CLAUDE.md forbids
  // useEffect outside the useMountEffect escape hatch — derived state,
  // event handlers, or ``key``-based remounts are the sanctioned tools.
  // (2) useMountEffect is per-instance; a real remount (error-boundary
  // recovery, hot reload, parent ``key`` change) creates a new instance
  // and re-runs this hook, so the remount path already works.
  const [siderHostEl, setSiderHostEl] = useState<HTMLElement | null>(null)
  useMountEffect(() => {
    if (resolvedSiderHostRef?.current) {
      setSiderHostEl(resolvedSiderHostRef.current)
    }
  })
  const api = useApiService()
  const { data: companyData } = api.useGetCompanyDetails()
  // Prefetch the data the embedded compose + note editors block on inside
  // their useEffect. Without this, clicking Apply right after a reload
  // mounts the modal before these queries resolve, leaving Quill
  // uninitialized (empty body, no toolbar) AND leaving the compose
  // form's from_id at 0 — which fails zod validation silently and the
  // submit handler never fires. We gate Apply on both queries below so
  // the dialog can't open early.
  const { isLoading: isUserOptionsLoading } = api.useGetUserOptions({
    onlyActive: true,
    withNoneOption: false,
  })
  const { data: integrationOptions, isLoading: isIntegrationOptionsLoading } =
    api.emailIntegration.useGetEmailIntegrationOptions()
  const isEditorDataReady =
    !isUserOptionsLoading && !isIntegrationOptionsLoading
  // Whether the rec owner has at least one connected email integration
  // capable of sending. ``can_send`` is the backend-derived "matching
  // email + synced + not expired" flag. Forwarded to the apply panel
  // so a ``send_email`` prepared action is force-disabled when there's
  // no integration.
  const hasEmailIntegration = (integrationOptions ?? []).some(
    (opt) => opt.original?.can_send
  )
  // Recommendation agents come from the backend AGENT_REGISTRY
  // (server/recommendations/pipeline/registry.py) via the company-details
  // payload. The frontend never hardcodes which signal_types belong to
  // which agent — that decision lives backend-side so the two cannot drift.
  const recommendationAgents = useMemo(
    () => companyData?.recommendation_agents ?? [],
    [companyData?.recommendation_agents]
  )
  const signalTypeToAgentId = useMemo(() => {
    const map: Record<string, string> = {}
    for (const agent of recommendationAgents) {
      for (const signalType of agent.signal_types) {
        map[signalType] = agent.id
      }
    }
    return map
  }, [recommendationAgents])
  // Agent sections used by the "Type" view. Filter out agents whose every
  // signal type is disabled — a fully-off agent has no actions to show
  // and rendering an empty section just adds visual noise.
  const filteredAgentSections = useMemo(
    () =>
      recommendationAgents
        .filter((a) => a.enabled)
        .map((a) => ({
          key: a.id,
          title: a.label,
          Icon: resolveAgentIcon(a.icon),
          iconColor: 'text-fg-secondary',
        })),
    [recommendationAgents]
  )
  const viewTabs = useMemo(
    () => [
      { key: 'priority' as ViewMode, title: 'Priority' },
      { key: 'signal' as ViewMode, title: 'Type' },
      { key: 'company' as ViewMode, title: 'Company' },
    ],
    []
  )

  const summaryQuery = useQuery<ActionSummaryData | null, Error>({
    queryKey: ['recommendations', userId ?? 'self'],
    queryFn: async () => {
      try {
        return await getLatestActionSummary(userId)
      } catch (err) {
        const axiosError = err as AxiosError<{ detail?: string }>
        if (axiosError.response?.status === 404) {
          return null
        }
        throw new Error(
          axiosError.response?.data?.detail ||
            axiosError.message ||
            'Failed to fetch recommendations'
        )
      }
    },
    refetchOnWindowFocus: false,
  })

  const summary = summaryQuery.data

  // Polling for GENERATING status
  useRecommendationsPolling({
    summary,
    refetch: summaryQuery.refetch,
    userId,
  })

  // Poll for recommendation refresh status — when CRM entities change, the
  // backend queues an LLM re-evaluation. This refetches the summary on completion.
  const { refreshStatus } = useAggregationRefresh({
    queryKey: ['recommendations-refresh-status'],
    queryFn: getRecommendationRefreshStatus,
    getIsRefreshing: (data) => data.is_refreshing,
    onRefetch: useCallback(() => summaryQuery.refetch(), [summaryQuery]),
    refetchInterval: 5000,
    enabled: userId === undefined,
  })

  // Set of fingerprints currently being re-evaluated — only those cards
  // get disabled/skeleton treatment, not the entire list.
  const refreshingFingerprints = useMemo(
    () => new Set(refreshStatus?.refreshing_fingerprints ?? []),
    [refreshStatus?.refreshing_fingerprints]
  )

  useEffect(() => {
    onSummaryLoaded?.(summary ?? null)
  }, [summary, onSummaryLoaded])

  useEffect(() => {
    setShowResolved(false)
  }, [summary?.id, userId])

  // Action click management
  const {
    handleActionClick,
    handleStatusClick,
    getStatusForAction,
    getAutoCompletedForAction,
  } = useActionClickManager({ summary })

  // The modal POSTs to the atomic apply endpoint itself; this callback
  // just refreshes the data once the backend has marked the rec resolved
  // and recorded the click. If the apply failed, the modal stays open
  // with an error toast and we never get here.
  const handleAppliedRecommendation = async (action: EnrichedAction) => {
    queryClient.removeQueries({
      queryKey: ['deal', String(action.deal_id)],
    })
    await summaryQuery.refetch()
  }

  // Non-action resolution path: user unchecked every prepared action
  // and clicked Apply, so we just mark the rec done client-side
  // (no server call to apply needed).
  const handleNoOpResolution = async (action: EnrichedAction) => {
    await handleStatusClick(action, 'done', false)
    await summaryQuery.refetch()
  }

  const allActions = useMemo(
    () => summary?.summary_data?.enriched_actions ?? [],
    [summary]
  )
  const { activeActions, resolvedActions } = useMemo(
    () => partitionActionsByStatus(allActions, getStatusForAction),
    [allActions, getStatusForAction]
  )

  const actionsByUrgency = useMemo(
    () => sortByUrgencyScore(activeActions),
    [activeActions]
  )
  const resolvedByUrgency = useMemo(
    () => sortByUrgencyScore(resolvedActions),
    [resolvedActions]
  )
  const actionsByAgent = useMemo(
    () => groupActionsByAgent(activeActions, signalTypeToAgentId),
    [activeActions, signalTypeToAgentId]
  )
  const resolvedActionsByAgent = useMemo(
    () => groupActionsByAgent(resolvedActions, signalTypeToAgentId),
    [resolvedActions, signalTypeToAgentId]
  )
  const actionsByCompany = useMemo(
    () => groupActionsByCompany(activeActions),
    [activeActions]
  )
  const resolvedActionsByCompany = useMemo(
    () => groupActionsByCompany(resolvedActions),
    [resolvedActions]
  )

  /**
   * Wrap a status-specific body (loading skeleton, error card, no-summary
   * card, FAILED card, GENERATING card) in the same shell the data-rich
   * path uses. The shell keeps ``headerSlot`` mounted so the user picker,
   * super-user regen button, and collapse control stay reachable in every
   * status — without this, hitting an error would strand a superuser on
   * a single failing summary with no way to switch to a different user
   * or kick a regen. The "Manage AI Agents" footer is anchored by the
   * parent layout (sibling, not child), so it survives independently.
   */
  const renderStatusShell = (body: React.ReactNode): React.ReactNode => (
    <div className={cn('flex flex-col min-h-0 flex-1', className)}>
      <div className="flex-shrink-0 pb-4 border-b border-line bg-surface -mx-6 px-6 flex flex-col gap-4">
        {headerSlot}
      </div>
      <div className="flex-1 overflow-auto pt-4 pb-6">{body}</div>
    </div>
  )

  if (summaryQuery.isLoading) {
    return renderStatusShell(
      <div className="flex flex-col gap-4">
        {/* Stats header placeholder */}
        <div className="flex items-center gap-4 animate-pulse">
          <div className="h-5 w-32 rounded bg-surface-disabled" />
          <div className="h-5 w-20 rounded bg-surface-disabled" />
          <div className="h-5 w-20 rounded bg-surface-disabled" />
        </div>
        {/* Card placeholders — same component used for the per-card
         *  applying state so the visuals are consistent. */}
        {[0, 1, 2, 3].map((i) => (
          <EnrichedActionCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  const queryError = summaryQuery.error as Error | undefined

  if (queryError) {
    return renderStatusShell(
      <div className="rounded-lg border border-error-border bg-error-bg p-6">
        <div className="flex items-center gap-2 text-error">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="font-medium">Error loading recommendations</span>
        </div>
        <p className="mt-2 text-sm text-error">{queryError.message}</p>
      </div>
    )
  }

  // No summary exists yet (status is null)
  if (!summary || summary.status === null) {
    return renderStatusShell(
      <div className="rounded-lg border border-line bg-surface p-6 shadow-sm">
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-fg-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-fg">
            No recommendations available
          </h3>
        </div>
      </div>
    )
  }

  if (summary.status === 'FAILED') {
    return renderStatusShell(
      <div className="rounded-lg border border-warning-border bg-warning-bg p-6">
        <div className="flex items-center gap-2 text-warning">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="font-medium">Summary generation failed</span>
        </div>
        {summary.error_message && (
          <p className="mt-2 text-sm text-warning">{summary.error_message}</p>
        )}
      </div>
    )
  }

  if (summary.status === 'GENERATING') {
    return renderStatusShell(
      <div className="rounded-lg border border-line bg-surface p-6">
        <div className="flex items-center gap-2 text-fg-tertiary">
          <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span className="font-medium">
            Generating your recommendations...
          </span>
        </div>
        <p className="mt-2 text-sm text-fg-secondary">
          This usually takes 10-30 seconds
        </p>
      </div>
    )
  }

  const hasActions = allActions.length > 0
  const hasActiveActions = activeActions.length > 0
  const hasResolvedActions = resolvedActions.length > 0
  const hasSignalData =
    Object.keys(actionsByAgent).length > 0 ||
    (showResolved && Object.keys(resolvedActionsByAgent).length > 0)

  /** Shared empty state shown when no active actions exist in any view. */
  const emptyState = (
    <div className="rounded-lg border border-dashed border-line bg-surface px-4 py-5 text-center">
      <p className="text-base font-medium text-fg">No active recommendations</p>
      <p className="mt-1 text-base text-fg-secondary">
        {hasResolvedActions
          ? 'Resolved items are hidden from your active queue.'
          : 'Your pipeline looks healthy.'}
      </p>
    </div>
  )

  /**
   * Render a group header matching the priority-view band style:
   * icon + uppercase label + muted count.
   */
  const renderGroupHeader = (
    Icon: React.ElementType,
    label: string,
    _count: number,
    iconClass: string
  ) => (
    <div className="flex items-center gap-1.5 mb-2">
      <Icon size={16} className={iconClass} />
      <span
        className={`text-sm font-semibold uppercase tracking-wide ${iconClass}`}
      >
        {label}
      </span>
    </div>
  )

  /**
   * Render the resolved-actions section for the side panel, grouped by
   * the user's currently-selected viewMode. Same data structures used
   * by the active-action sections, just sourced from the resolved
   * memos so the panel mirrors the user's mental model of how cards
   * are organized.
   */
  const renderResolvedSections = () => {
    if (viewMode === 'priority') {
      return (
        <div className="space-y-4">
          {URGENCY_BANDS.map(({ key, label }) => {
            const UrgencyIcon = URGENCY_ICONS[key]
            const bandActions = resolvedByUrgency.filter(
              (a) => a.urgency === key
            )
            if (bandActions.length === 0) return null
            return (
              <div key={key}>
                {renderGroupHeader(
                  UrgencyIcon,
                  label,
                  bandActions.length,
                  URGENCY_COLORS[key]
                )}
                {renderCards(bandActions, `priority-resolved-${key}`, false)}
              </div>
            )
          })}
        </div>
      )
    }
    if (viewMode === 'signal') {
      return (
        <div className="space-y-4">
          {filteredAgentSections.map(({ key, title, Icon, iconColor }) => {
            const sectionActions = resolvedActionsByAgent[key] ?? []
            if (sectionActions.length === 0) return null
            return (
              <div key={`resolved-${key}`}>
                {renderGroupHeader(
                  Icon,
                  title,
                  sectionActions.length,
                  iconColor
                )}
                {renderCards(sectionActions, `agent-resolved-${key}`, false)}
              </div>
            )
          })}
        </div>
      )
    }
    // company view
    return (
      <div className="space-y-4">
        {resolvedActionsByCompany.map((company) => (
          <div key={`resolved-${company.companyId}`}>
            {renderGroupHeader(
              Building2,
              company.companyName,
              company.actions.length,
              'text-fg-secondary'
            )}
            {renderCards(
              company.actions,
              `company-resolved-${company.companyId}`,
              false
            )}
          </div>
        ))}
      </div>
    )
  }

  /**
   * Render a list of action cards for a group.
   *
   * ``dimWhenResolved`` defaults to true, which is the right setting
   * for the main active-list (already-resolved cards fade visually so
   * the user's eye can skip them). The recently-completed side panel
   * passes ``false`` so its cards render at full opacity.
   */
  const renderCards = (
    actions: EnrichedAction[],
    keyPrefix: string,
    dimWhenResolved: boolean = true
  ) => (
    <div className="space-y-4">
      {actions.map((action, index) => (
        <EnrichedActionCard
          key={`${keyPrefix}-${action.signal_fingerprint ?? `${action.deal_id}-${index}`}`}
          action={action}
          dimWhenResolved={dimWhenResolved}
          onCardClick={() =>
            handleActionClick(action, action.signal_type, action.action_type)
          }
          readOnly={!!userId}
          isRefreshing={
            // Disable Apply/Dismiss while any of:
            //  (a) the per-rec refresh poller flagged this signal (the
            //      LLM is re-evaluating it),
            //  (b) we're refetching the whole summary (e.g. right after
            //      a successful apply, before the new resolution state
            //      lands), or
            //  (c) the user-options query the embedded Quill editors
            //      block on hasn't resolved yet — without this, the
            //      modal can mount before Quill initializes and shows
            //      empty body editors with no toolbar.
            refreshingFingerprints.has(action.signal_fingerprint ?? '') ||
            summaryQuery.isFetching ||
            !isEditorDataReady
          }
          onStatusClick={(status) => handleStatusClick(action, status)}
          actionStatus={getStatusForAction(action)}
          actionAutoCompleted={getAutoCompletedForAction(action)}
          onApplied={() => handleAppliedRecommendation(action)}
          onNoOpResolve={() => handleNoOpResolution(action)}
          onApplyClick={(a) => setActivePanel({ kind: 'apply-rec', action: a })}
          recommendationAgents={recommendationAgents}
        />
      ))}
    </div>
  )

  // No actions in any view → reuse the status shell so the user picker,
  // super-user regen, and collapse stay in the same fixed-position header
  // they occupy in error / loading / generating states. The data-rich
  // path below has its own header-with-stats wrapper since it also needs
  // to layer in SummaryStatsHeader and the view-mode tabs.
  if (!hasActions) {
    return renderStatusShell(
      <div className="rounded-lg border border-dashed border-line bg-surface p-6 text-center">
        <p className="text-base font-medium text-fg">
          No active recommendations
        </p>
        <p className="mt-1 text-base text-fg-secondary">
          Your pipeline looks healthy.
        </p>
      </div>
    )
  }

  // ``hasActions`` is true at this point — the no-actions branch above
  // returned early via ``renderStatusShell``. The wrapper below is the
  // data-rich layout: pinned header (slot + stats + view controls) and
  // a scrollable body of grouped action cards.
  return (
    <div className={cn('flex flex-col min-h-0 flex-1', className)}>
      {/* Fixed header zone: slot + stats + view controls */}
      <div className="flex-shrink-0 pb-4 border-b border-line bg-surface -mx-6 px-6 flex flex-col gap-4">
        {headerSlot}
        {summary.summary_data && (
          <SummaryStatsHeader
            data={summary.summary_data}
            visibleActions={activeActions}
            viewMode={viewMode}
            companiesCount={actionsByCompany.length}
            recommendationAgents={recommendationAgents}
          />
        )}

        {(hasSignalData || hasResolvedActions || summary.created) && (
          <div className="flex items-end justify-between gap-3">
            {/* Updating indicator — only visible while at least one card
                is in skeleton/refresh state. Hidden by default so the
                row reads cleanly; the right-side controls anchor the
                row's height. */}
            {(summaryQuery.isFetching ||
              refreshingFingerprints.size > 0 ||
              !isEditorDataReady) && (
              <span className="inline-flex items-center gap-1.5 text-sm text-fg-muted">
                <Loader2 size={14} className="animate-spin" />
                Updating…
              </span>
            )}
            <div className="ml-auto flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowResolved(!showResolved)}
                className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-3 py-1.5 text-sm text-fg-secondary transition-colors hover:bg-surface-hover hover:text-fg"
              >
                <Check
                  size={14}
                  strokeWidth={3}
                  className="text-fb-dark-green"
                />
                Recently completed ({resolvedActions.length} /{' '}
                {activeActions.length + resolvedActions.length})
              </button>
              <div className="flex items-center border border-line rounded-md overflow-hidden bg-surface flex-shrink-0">
                {viewTabs.map((tab, i) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setViewMode(tab.key)}
                    className={cn(
                      'px-3 py-1.5 text-sm transition-colors',
                      i > 0 && 'border-l border-line',
                      viewMode === tab.key
                        ? 'bg-surface-alt text-fg font-semibold'
                        : 'text-fg-secondary hover:bg-surface-hover'
                    )}
                  >
                    {tab.title}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scrollable action cards */}
      <div className="flex-1 overflow-auto space-y-4 pt-4 pb-6">
        {/* ── Priority view ── */}
        {viewMode === 'priority' && (
          <div className="space-y-4">
            {hasActiveActions ? (
              <div className="space-y-4">
                {URGENCY_BANDS.map(({ key, label }) => {
                  const UrgencyIcon = URGENCY_ICONS[key]
                  const bandActions = actionsByUrgency.filter(
                    (a) => a.urgency === key
                  )
                  if (bandActions.length === 0) return null
                  return (
                    <div key={key}>
                      {renderGroupHeader(
                        UrgencyIcon,
                        label,
                        bandActions.length,
                        URGENCY_COLORS[key]
                      )}
                      {renderCards(bandActions, `priority-${key}`)}
                    </div>
                  )
                })}
              </div>
            ) : (
              emptyState
            )}
          </div>
        )}

        {/* ── Type (agent) view ── */}
        {viewMode === 'signal' && (
          <div className="space-y-4">
            {hasActiveActions ? (
              <div className="space-y-4">
                {filteredAgentSections.map(
                  ({ key, title, Icon, iconColor }) => {
                    const sectionActions = actionsByAgent[key] ?? []
                    if (sectionActions.length === 0) return null
                    return (
                      <div key={key}>
                        {renderGroupHeader(
                          Icon,
                          title,
                          sectionActions.length,
                          iconColor
                        )}
                        {renderCards(sectionActions, `agent-${key}`)}
                      </div>
                    )
                  }
                )}
              </div>
            ) : (
              emptyState
            )}
          </div>
        )}

        {/* ── Company view ── */}
        {viewMode === 'company' && (
          <div className="space-y-4">
            {hasActiveActions ? (
              <div className="space-y-4">
                {actionsByCompany.map((company) => (
                  <div key={company.companyId}>
                    {renderGroupHeader(
                      Building2,
                      company.companyName,
                      company.actions.length,
                      'text-fg-secondary'
                    )}
                    {renderCards(
                      company.actions,
                      `company-${company.companyId}`
                    )}
                  </div>
                ))}
              </div>
            ) : (
              emptyState
            )}
          </div>
        )}
      </div>

      {/* Right-panel content — content portaled into the parent-
          provided host element so it lives as a flex sibling of the
          main content (matches the old Action Center pattern). The
          host's width animates between 0 and 945px; ``overflow:
          hidden`` on the host clips the panel cleanly during the
          transition. The panel itself has no positioning — it just
          fills the host. */}
      {siderHostEl &&
        createPortal(
          <>
            {activePanel.kind === 'recently-completed' && (
              <RightPanel
                onClose={() => setActivePanel({ kind: 'closed' })}
                header={
                  <>
                    <Check
                      size={20}
                      strokeWidth={3}
                      className="text-fb-dark-green flex-shrink-0"
                    />
                    <h3 className="text-xl font-semibold text-fg-secondary truncate">
                      Recently completed ({resolvedActions.length} /{' '}
                      {activeActions.length + resolvedActions.length})
                    </h3>
                  </>
                }
              >
                {hasResolvedActions ? (
                  renderResolvedSections()
                ) : (
                  <p className="text-base text-fg-secondary text-center mt-8">
                    Nothing completed yet.
                  </p>
                )}
              </RightPanel>
            )}
            {activePanel.kind === 'apply-rec' && (
              <ApplyRecommendationPanel
                onClose={() => setActivePanel({ kind: 'closed' })}
                action={activePanel.action}
                preparedActions={activePanel.action.prepared_actions ?? []}
                onApply={async (formData, count) => {
                  const action = activePanel.action
                  if (action.id == null) return
                  // Close the panel immediately and fire the apply in
                  // the background — same UX the modal had. The card's
                  // refresh poller will reflect the new state once the
                  // backend resolves.
                  setActivePanel({ kind: 'closed' })
                  try {
                    await applyRecommendation(action.id, formData)
                    notifySuccess(
                      `${count} ${count === 1 ? 'action' : 'actions'} applied`
                    )
                    handleAppliedRecommendation(action)
                  } catch {
                    // baseAPICall already surfaced an error toast.
                  }
                }}
                onNoOpResolve={() => {
                  handleNoOpResolution(activePanel.action)
                }}
                mutuallyExclusiveActions={
                  summary?.summary_data?.mutually_exclusive_actions ?? []
                }
                hasEmailIntegration={hasEmailIntegration}
                recommendationAgents={recommendationAgents}
              />
            )}
          </>,
          siderHostEl
        )}
    </div>
  )
}
